import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { StorageTTLConfig } from '../config/storage-ttl-config';

export interface StorageError {
  type: 'quota_exceeded' | 'indexeddb_unavailable' | 'localstorage_unavailable' | 'critical_data_loss';
  message: string;
  key?: string;
  timestamp: number;
  critical: boolean;
}

/**
 * Service to handle storage-related errors and provide fallback strategies
 * @author AI Assistant
 * @date 2025-01-17
 */
@Injectable({
  providedIn: 'root'
})
export class StorageErrorHandlerService {
  private errorsSubject = new BehaviorSubject<StorageError[]>([]);
  public errors$: Observable<StorageError[]> = this.errorsSubject.asObservable();

  private criticalErrorsSubject = new BehaviorSubject<StorageError | null>(null);
  public criticalErrors$: Observable<StorageError | null> = this.criticalErrorsSubject.asObservable();

  constructor() {}

  /**
   * Handle a storage error
   * @param error - Error object or string
   * @param key - Key that caused the error
   * @param isCritical - Whether this error blocks the application
   */
  handleStorageError(error: Error | string, key?: string, isCritical: boolean = false): void {
    const errorMessage = typeof error === 'string' ? error : error.message;
    let errorType: StorageError['type'] = 'indexeddb_unavailable';

    // Determine error type based on error message
    if (errorMessage.includes('QuotaExceededError') || errorMessage.includes('quota')) {
      errorType = 'quota_exceeded';
    } else if (errorMessage.includes('localStorage')) {
      errorType = 'localstorage_unavailable';
    } else if (isCritical && (key?.includes('construct-') || key?.includes('form-fields-'))) {
      errorType = 'critical_data_loss';
    }

    const storageError: StorageError = {
      type: errorType,
      message: errorMessage,
      key,
      timestamp: Date.now(),
      critical: isCritical
    };

    // Add to errors list
    const currentErrors = this.errorsSubject.value;
    const newErrors = [storageError, ...currentErrors].slice(0, 50); // Keep last 50 errors
    this.errorsSubject.next(newErrors);

    // If critical, emit separately
    if (isCritical) {
      this.criticalErrorsSubject.next(storageError);
    }

    console.error('Storage Error:', storageError);
  }

  /**
   * Get recovery suggestions for a storage error
   * @param error - The storage error
   * @returns Array of suggested actions
   */
  getRecoverySuggestions(error: StorageError): string[] {
    const suggestions: string[] = [];

    switch (error.type) {
      case 'quota_exceeded':
        suggestions.push(
          'Liberar espacio eliminando datos innecesarios del navegador',
          'Cerrar otras pestañas del navegador',
          'Limpiar caché y datos temporales',
          'Usar modo incógnito/privado'
        );
        break;

      case 'indexeddb_unavailable':
        suggestions.push(
          'Verificar que IndexedDB esté habilitado en el navegador',
          'Actualizar el navegador a la última versión',
          'Intentar en modo incógnito',
          'Reiniciar el navegador'
        );
        break;

      case 'localstorage_unavailable':
        suggestions.push(
          'Verificar que localStorage esté habilitado',
          'Deshabilitar modo privado/incógnito',
          'Limpiar datos del sitio web',
          'Verificar configuración de privacidad del navegador'
        );
        break;

      case 'critical_data_loss':
        suggestions.push(
          'Contactar soporte técnico inmediatamente',
          'No cerrar la aplicación hasta resolver el problema',
          'Tomar captura de pantalla del error',
          'Intentar exportar datos manualmente'
        );
        break;
    }

    return suggestions;
  }

  /**
   * Get user-friendly error message
   * @param error - The storage error
   * @returns User-friendly message in Spanish
   */
  getUserFriendlyMessage(error: StorageError): string {
    switch (error.type) {
      case 'quota_exceeded':
        return 'El almacenamiento del navegador está lleno. Por favor, libere espacio para continuar.';
      
      case 'indexeddb_unavailable':
        return 'El sistema de almacenamiento avanzado no está disponible. La aplicación continuará con funcionalidad limitada.';
      
      case 'localstorage_unavailable':
        return 'El almacenamiento local no está disponible. Algunas funciones pueden no funcionar correctamente.';
      
      case 'critical_data_loss':
        return 'Error crítico: No se pudo guardar información importante de la encuesta. Por favor, contacte soporte técnico.';
      
      default:
        return 'Se produjo un error de almacenamiento. La aplicación intentará continuar normalmente.';
    }
  }

  /**
   * Check if there are any critical errors
   */
  hasCriticalErrors(): boolean {
    return this.errorsSubject.value.some(error => error.critical);
  }

  /**
   * Get all errors of a specific type
   * @param type - Error type to filter by
   */
  getErrorsByType(type: StorageError['type']): StorageError[] {
    return this.errorsSubject.value.filter(error => error.type === type);
  }

  /**
   * Clear all errors
   */
  clearErrors(): void {
    this.errorsSubject.next([]);
    this.criticalErrorsSubject.next(null);
  }

  /**
   * Clear errors older than specified time
   * @param olderThanMs - Time in milliseconds (defaults to 24 hours using TTL config)
   */
  clearOldErrors(olderThanMs: number = StorageTTLConfig.STORAGE_ERRORS_TTL): void {
    const cutoff = Date.now() - olderThanMs;
    const recentErrors = this.errorsSubject.value.filter(error => error.timestamp > cutoff);
    this.errorsSubject.next(recentErrors);
  }

  /**
   * Test storage capabilities with automatic cleanup
   */
  async testStorageCapabilities(): Promise<{
    localStorage: boolean;
    indexedDB: boolean;
    available: boolean;
    issues: string[];
  }> {
    const result = {
      localStorage: false,
      indexedDB: false,
      available: false,
      issues: [] as string[]
    };

    // Test localStorage with automatic cleanup
    let localStorageTestKey: string | null = null;
    try {
      localStorageTestKey = '_storage_test_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem(localStorageTestKey, 'test');
      localStorage.removeItem(localStorageTestKey); // Limpieza inmediata
      localStorageTestKey = null; // Mark as cleaned
      result.localStorage = true;
    } catch (error) {
      result.issues.push('localStorage no disponible: ' + error.message);
      // Cleanup in case of error
      if (localStorageTestKey) {
        try {
          localStorage.removeItem(localStorageTestKey);
        } catch (cleanupError) {
          console.warn('Failed to cleanup localStorage test key:', cleanupError);
        }
      }
    }

    // Test IndexedDB with automatic cleanup
    let indexedDBName: string | null = null;
    try {
      if ('indexedDB' in window) {
        indexedDBName = '_storage_test_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        
        // Simple test to see if we can open a database
        const request = indexedDB.open(indexedDBName, 1);
        
        await new Promise((resolve, reject) => {
          request.onsuccess = () => {
            const db = request.result;
            db.close(); // Close database
            
          // Delete test database immediately
            const deleteRequest = indexedDB.deleteDatabase(indexedDBName!);
            deleteRequest.onsuccess = () => {
              // console.log(`Test database ${indexedDBName} deleted successfully`);
              resolve(true);
            };
            deleteRequest.onerror = () => {
              console.warn(`Failed to delete test database ${indexedDBName}:`, deleteRequest.error);
              resolve(true); // Don't fail the test if cleanup fails
            };
            deleteRequest.onblocked = () => {
              console.warn(`Deletion of test database ${indexedDBName} was blocked`);
              resolve(true); // Don't fail the test if cleanup is blocked
            };
          };
          
          request.onerror = () => reject(request.error);
          request.onblocked = () => reject(new Error('IndexedDB blocked'));
        });
        
        result.indexedDB = true;
      } else {
        result.issues.push('IndexedDB no está disponible en este navegador');
      }
    } catch (error) {
      result.issues.push('IndexedDB no disponible: ' + error.message);
      
      // Cleanup in case of error
      if (indexedDBName) {
        try {
          const cleanupRequest = indexedDB.deleteDatabase(indexedDBName);
          cleanupRequest.onsuccess = () => console.log(`Cleanup: Test database ${indexedDBName} deleted after error`);
          cleanupRequest.onerror = () => console.warn(`Failed to cleanup test database ${indexedDBName} after error`);
        } catch (cleanupError) {
          console.warn('Failed to cleanup IndexedDB test database:', cleanupError);
        }
      }
    }

    result.available = result.localStorage || result.indexedDB;
    return result;
  }

  /**
   * Generate a detailed error report
   */
  generateErrorReport(): {
    summary: string;
    errors: StorageError[];
    recommendations: string[];
    timestamp: number;
  } {
    const errors = this.errorsSubject.value;
    const criticalCount = errors.filter(e => e.critical).length;
    const totalCount = errors.length;

    let summary = `Total de errores: ${totalCount}`;
    if (criticalCount > 0) {
      summary += ` (${criticalCount} críticos)`;
    }

    // Generate recommendations based on error patterns
    const recommendations: string[] = [];
    const errorTypes = new Set(errors.map(e => e.type));

    if (errorTypes.has('quota_exceeded')) {
      recommendations.push('Implementar limpieza automática de datos antiguos');
      recommendations.push('Informar al usuario sobre el estado del almacenamiento');
    }

    if (errorTypes.has('critical_data_loss')) {
      recommendations.push('Implementar respaldo automático de datos críticos');
      recommendations.push('Revisar el proceso de manejo de errores');
    }

    if (errorTypes.has('indexeddb_unavailable')) {
      recommendations.push('Verificar compatibilidad del navegador');
      recommendations.push('Implementar fallbacks más robustos');
    }

    return {
      summary,
      errors,
      recommendations,
      timestamp: Date.now()
    };
  }

  /**
   * Generate a unique test key that's easily identifiable and can be cleaned up
   * @param prefix - Prefix for the test key
   */
  private generateTestKey(prefix: string = '_storage_test_'): string {
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substr(2, 9);
    return `${prefix}${timestamp}_${randomId}`;
  }

  /**
   * Cleanup any orphaned test keys/databases from localStorage
   * This can be called periodically or on app startup
   */
  cleanupOrphanedTestData(): void {
    try {
      let cleanedCount = 0;
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && key.startsWith('_storage_test_')) {
          const timestamp = this.extractTimestampFromKey(key);
          // Remove test keys older than 1 hour
          if (timestamp && Date.now() - timestamp > 60 * 60 * 1000) {
            localStorage.removeItem(key);
            cleanedCount++;
            // console.log(`Cleaned up orphaned test key: ${key}`);
          }
        }
      }
      
      if (cleanedCount > 0) {
        // console.log(`Cleanup completed: Removed ${cleanedCount} orphaned test keys`);
      }
    } catch (error) {
      console.warn('Error cleaning up orphaned localStorage test keys:', error);
    }
  }

  /**
   * Extract timestamp from test key for cleanup purposes
   * @param key - The test key to extract timestamp from
   */
  private extractTimestampFromKey(key: string): number | null {
    const match = key.match(/_(\d+)_/);
    return match ? parseInt(match[1], 10) : null;
  }

  /**
   * Test localStorage with automatic cleanup using improved methods
   */
  async testLocalStorageImproved(): Promise<{ success: boolean; error?: string }> {
    let testKey: string | null = null;
    try {
      testKey = this.generateTestKey();
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return { success: true };
    } catch (error) {
      // Cleanup on error
      if (testKey) {
        try {
          localStorage.removeItem(testKey);
        } catch (cleanupError) {
          console.warn('Failed to cleanup localStorage test key:', cleanupError);
        }
      }
      return { success: false, error: error.message };
    }
  }

  /**
   * Test IndexedDB with automatic cleanup using improved methods
   */
  async testIndexedDBImproved(): Promise<{ success: boolean; error?: string }> {
    if (!('indexedDB' in window)) {
      return { success: false, error: 'IndexedDB not available in this browser' };
    }

    let testDBName: string | null = null;
    try {
      testDBName = this.generateTestKey('_test_db_');
      
      return new Promise((resolve) => {
        const request = indexedDB.open(testDBName!, 1);
        
        request.onsuccess = () => {
          const db = request.result;
          db.close();
          
            // Delete test database immediately
          const deleteRequest = indexedDB.deleteDatabase(testDBName!);
          deleteRequest.onsuccess = () => {
            // console.log(`Test database ${testDBName} cleaned up successfully`);
            resolve({ success: true });
          };
          deleteRequest.onerror = () => {
            console.warn(`Failed to cleanup test database ${testDBName}`);
            resolve({ success: true }); // Still successful even if cleanup fails
          };
          deleteRequest.onblocked = () => {
            console.warn(`Cleanup of test database ${testDBName} was blocked`);
            resolve({ success: true });
          };
        };
        
        request.onerror = () => {
          resolve({ success: false, error: request.error?.message || 'IndexedDB test failed' });
        };
        
        request.onblocked = () => {
          resolve({ success: false, error: 'IndexedDB test blocked' });
        };
      });
    } catch (error) {
      // Cleanup on error
      if (testDBName) {
        try {
          indexedDB.deleteDatabase(testDBName);
        } catch (cleanupError) {
          console.warn('Failed to cleanup test database:', cleanupError);
        }
      }
      return { success: false, error: error.message };
    }
  }
}

